import { Virtualizer } from "@tanstack/react-virtual";
import { RefObject, useLayoutEffect } from "react";

import { TreeNode } from "utils/tree";
import { SpaceWithParentAccessLevel } from "views/Account/Spaces/types";

import type { SpacesListCollapseAnchor, SpacesListGetCollapseAnchor } from "../index";

type SpacesListNode = TreeNode<SpaceWithParentAccessLevel>;

// Viewport top of the row's name cell, or null if it isn't rendered.
const readCellTop = (container: HTMLElement, id: string) => {
  const cell = container.querySelector<HTMLElement>(`[id="${CSS.escape(id)}-NAME"]`);
  return cell ? cell.getBoundingClientRect().top : null;
};

type UseCollapseAnchorParams = {
  nodes: SpacesListNode[];
  tableRef: RefObject<HTMLDivElement | null>;
  collapseAnchorRef: RefObject<SpacesListCollapseAnchor | null>;
  getCollapseAnchorRef: RefObject<SpacesListGetCollapseAnchor | null>;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
};

/**
 * Keeps the toggled row visually in place across a collapse. Removing a subtree shifts the rows
 * above the fold, moving the toggled row on screen even though `scrollTop` doesn't change. The
 * virtualizer's coordinates can't be trusted here (estimate vs. real height, plus the forced
 * sticky spacer), so we anchor on the rendered cell's real `getBoundingClientRect`.
 */
export const useSpacesListCollapseAnchor = ({
  nodes,
  tableRef,
  collapseAnchorRef,
  getCollapseAnchorRef,
  virtualizer,
}: UseCollapseAnchorParams) => {
  // Capture the toggled row's on-screen top at click time, before the collapse re-renders.
  useLayoutEffect(() => {
    getCollapseAnchorRef.current = (id) => {
      const container = tableRef.current;

      if (!container) {
        return null;
      }

      const cellTop = readCellTop(container, id);

      if (cellTop === null) {
        return null;
      }

      const containerTop = container.getBoundingClientRect().top;

      return { id, cellViewportTop: cellTop - containerTop };
    };
  });

  // After the re-render, measure how far the cell actually moved and nudge scrollTop by the same
  // amount so it stays put. Once now (before paint) and again next frame to catch any late shift
  // as the sticky transforms re-settle.
  useLayoutEffect(() => {
    const anchor = collapseAnchorRef.current;

    if (!anchor) {
      return;
    }

    collapseAnchorRef.current = null;

    const align = () => {
      const container = tableRef.current;

      if (!container) {
        return;
      }

      const cellTop = readCellTop(container, anchor.id);

      if (cellTop === null) {
        return;
      }

      const containerTop = container.getBoundingClientRect().top;
      const delta = cellTop - containerTop - anchor.cellViewportTop;

      if (delta !== 0) {
        virtualizer.scrollToOffset(container.scrollTop + delta);
      }
    };

    align();
    requestAnimationFrame(align);
  }, [nodes, collapseAnchorRef, tableRef, virtualizer]);
};
